import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';


const LocationFilter = ({ 
  selectedRegion, 
  selectedDepartment, 
  searchRadius, 
  useCurrentLocation,
  onRegionChange, 
  onDepartmentChange, 
  onRadiusChange,
  onLocationToggle 
}) => {
  const [currentLocation, setCurrentLocation] = useState(null);
  const [isDetecting, setIsDetecting] = useState(false);
  const [availableDepartments, setAvailableDepartments] = useState([]);

  const regions = [
    { value: 'centre', label: 'Centre' },
    { value: 'littoral', label: 'Littoral' },
    { value: 'ouest', label: 'Ouest' },
    { value: 'nord-ouest', label: 'Nord-Ouest / Northwest' },
    { value: 'sud-ouest', label: 'Sud-Ouest / Southwest' },
    { value: 'nord', label: 'Nord / North' },
    { value: 'extreme-nord', label: 'Extrême-Nord / Far North' },
    { value: 'adamaoua', label: 'Adamaoua' },
    { value: 'est', label: 'Est / East' },
    { value: 'sud', label: 'Sud / South' }
  ];

  const departmentsByRegion = {
    centre: [
      { value: 'mfoundi', label: 'Mfoundi (Yaoundé)' },
      { value: 'lekie', label: 'Lékié' },
      { value: 'mefou-et-afamba', label: 'Mefou-et-Afamba' },
      { value: 'mefou-et-akono', label: 'Mefou-et-Akono' },
      { value: 'mbam-et-inoubou', label: 'Mbam-et-Inoubou' }
    ],
    littoral: [
      { value: 'wouri', label: 'Wouri (Douala)' },
      { value: 'nkam', label: 'Nkam' },
      { value: 'mungo', label: 'Mungo' },
      { value: 'sanaga-maritime', label: 'Sanaga-Maritime' }
    ],
    ouest: [
      { value: 'mifi', label: 'Mifi (Bafoussam)' },
      { value: 'bamboutos', label: 'Bamboutos' },
      { value: 'haut-nkam', label: 'Haut-Nkam' },
      { value: 'koung-khi', label: 'Koung-Khi' },
      { value: 'menoua', label: 'Menoua' }
    ],
    'nord-ouest': [
      { value: 'mezam', label: 'Mezam (Bamenda)' },
      { value: 'bui', label: 'Bui' },
      { value: 'donga-mantung', label: 'Donga-Mantung' },
      { value: 'menchum', label: 'Menchum' }
    ],
    'sud-ouest': [
      { value: 'fako', label: 'Fako (Buea)' },
      { value: 'kupe-manenguba', label: 'Kupe-Manenguba' },
      { value: 'lebialem', label: 'Lebialem' },
      { value: 'manyu', label: 'Manyu' },
      { value: 'meme', label: 'Meme' },
      { value: 'ndian', label: 'Ndian' }
    ]
  };

  const radiusOptions = [
    { value: '5', label: '5 km' },
    { value: '10', label: '10 km' },
    { value: '25', label: '25 km' },
    { value: '50', label: '50 km' },
    { value: '100', label: '100 km' },
    { value: '200', label: '200 km' },
    { value: 'all', label: 'Tout le Cameroun / All Cameroon' }
  ];

  useEffect(() => {
    if (selectedRegion && departmentsByRegion?.[selectedRegion]) {
      setAvailableDepartments(departmentsByRegion?.[selectedRegion]);
    } else {
      setAvailableDepartments([]);
      onDepartmentChange('');
    }
  }, [selectedRegion, onDepartmentChange]);

  const detectCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('La géolocalisation n\'est pas supportée / Geolocation not supported');
      return;
    }

    setIsDetecting(true);
    navigator.geolocation?.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position?.coords;
        const mockLocation = {
          region: 'littoral',
          department: 'wouri',
          city: 'Douala',
          coordinates: { lat: latitude, lng: longitude }
        };
        
        setCurrentLocation(mockLocation);
        onRegionChange(mockLocation?.region);
        onDepartmentChange(mockLocation?.department);
        onLocationToggle(true);
        setIsDetecting(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        setIsDetecting(false);
        alert('Impossible de détecter votre position / Unable to detect location');
      }
    );
  };

  return (
    <div className="space-y-4">
      {/* Current Location Detection */}
      <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
        <div className="flex items-center space-x-2">
          <Icon 
            name="MapPin" 
            size={16} 
            className={currentLocation ? 'text-success' : 'text-muted-foreground'} 
          />
          <div>
            <p className="text-sm font-medium text-foreground">
              {currentLocation 
                ? `${currentLocation?.city}, ${currentLocation?.department}` 
                : 'Position non détectée / Location not detected'
              }
            </p>
            {currentLocation && (
              <p className="text-xs text-muted-foreground">
                Utilisation de votre position actuelle / Using current location
              </p>
            )}
          </div>
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={detectCurrentLocation}
          disabled={isDetecting}
          loading={isDetecting}
          iconName={isDetecting ? "Loader2" : "Crosshair"}
          iconSize={16}
        >
          {isDetecting ? 'Détection...' : 'Ma position'}
        </Button>
      </div>
      {/* Region Selection */}
      <Select
        label="Région / Region"
        placeholder="Sélectionner une région / Select region"
        options={regions}
        value={selectedRegion}
        onChange={onRegionChange}
        searchable
        clearable
        description="Choisissez votre région / Choose your region"
      />
      {/* Department Selection */}
      {selectedRegion && (
        <Select
          label="Département / Department"
          placeholder="Sélectionner un département / Select department"
          options={availableDepartments}
          value={selectedDepartment}
          onChange={onDepartmentChange}
          searchable
          clearable
          description="Choisissez votre département / Choose your department"
        />
      )}
      {/* Search Radius */}
      <Select
        label="Rayon de recherche / Search radius"
        placeholder="Sélectionner un rayon / Select radius"
        options={radiusOptions}
        value={searchRadius}
        onChange={onRadiusChange}
        description="Distance maximale de recherche / Maximum search distance"
      />
      {/* Location Summary */}
      {(selectedRegion || selectedDepartment) && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
          <div className="flex items-center space-x-2">
            <Icon name="MapPin" size={16} className="text-primary" />
            <div>
              <p className="text-sm font-medium text-foreground">
                Zone de recherche / Search area
              </p>
              <p className="text-xs text-muted-foreground">
                {selectedDepartment 
                  ? `${availableDepartments?.find(d => d?.value === selectedDepartment)?.label}, ${regions?.find(r => r?.value === selectedRegion)?.label}`
                  : selectedRegion 
                    ? regions?.find(r => r?.value === selectedRegion)?.label
                    : 'Non spécifiée / Not specified'
                }
                {searchRadius && searchRadius !== 'all' && ` - Rayon: ${searchRadius} km`}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationFilter;